import { Calendar, dateFnsLocalizer, Views } from 'react-big-calendar';
import { format, parse, startOfWeek, getDay } from 'date-fns';
import { tr } from 'date-fns/locale';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { useState, useCallback, useMemo } from 'react';
import clsx from 'clsx';

// Türkçe localizer
const locales = { 'tr': tr };
const localizer = dateFnsLocalizer({
    format,
    parse,
    startOfWeek: () => startOfWeek(new Date(), { weekStartsOn: 1 }), // Pazartesi başlangıç
    getDay,
    locales,
});

// Türkçe mesajlar
const messages = {
    allDay: 'Tüm gün',
    previous: 'Önceki',
    next: 'Sonraki',
    today: 'Bugün',
    month: 'Ay',
    week: 'Hafta',
    day: 'Gün',
    agenda: 'Ajanda',
    date: 'Tarih',
    time: 'Saat',
    event: 'Etkinlik',
    noEventsInRange: 'Bu aralıkta etkinlik yok.',
    showMore: (total) => `+${total} daha`,
};

// Event component (özelleştirilebilir)
const EventComponent = ({ event }) => (
    <div className="flex items-center gap-1 text-xs">
        {event.icon && <span>{event.icon}</span>}
        <span className="truncate">{event.title}</span>
    </div>
);

/**
 * BigCalendar - react-big-calendar için reusable wrapper component
 * 
 * @param {Object} props
 * @param {Array} props.events - Etkinlik listesi [{title, start, end, ...}]
 * @param {Function} props.onSelectEvent - Etkinlik tıklama callback
 * @param {Function} props.onSelectSlot - Boş slot tıklama callback (yeni etkinlik için)
 * @param {Function} props.onNavigate - Tarih değişikliği callback
 * @param {string} props.defaultView - Varsayılan görünüm ('month' | 'week' | 'day' | 'agenda')
 * @param {Date} props.defaultDate - Varsayılan tarih
 * @param {boolean} props.selectable - Slot seçimi aktif mi
 * @param {string} props.height - Takvim yüksekliği (CSS)
 * @param {Object} props.eventColors - Kategori bazlı renk haritası
 * @param {string} props.className - Ek CSS sınıfları
 */
export default function BigCalendar({
    events = [],
    onSelectEvent,
    onSelectSlot,
    onNavigate,
    defaultView = 'month',
    defaultDate = new Date(),
    selectable = true,
    height = '600px',
    eventColors = {},
    className = '',
    ...rest
}) {
    const [view, setView] = useState(defaultView);
    const [date, setDate] = useState(defaultDate);

    // Event stil fonksiyonu
    const eventPropGetter = useCallback((event) => {
        const category = event.category || 'default';
        const colorConfig = eventColors[category] || { bg: 'bg-primary', text: 'text-white' };

        // CSS class'ları kullanarak stil
        return {
            className: clsx(
                'rounded-md border-none shadow-sm',
                colorConfig.bg,
                colorConfig.text
            ),
            style: {
                backgroundColor: colorConfig.bgColor || undefined,
                color: colorConfig.textColor || undefined,
                border: 'none',
            }
        };
    }, [eventColors]);

    // Slot stil fonksiyonu
    const slotPropGetter = useCallback((date) => {
        const isToday = new Date().toDateString() === date.toDateString();
        return {
            className: isToday ? 'bg-primary-50' : '',
        };
    }, []);

    // Navigate handler
    const handleNavigate = useCallback((newDate) => {
        setDate(newDate);
        onNavigate?.(newDate);
    }, [onNavigate]);

    // View change handler
    const handleViewChange = useCallback((newView) => {
        setView(newView);
    }, []);

    // Slot selection handler
    const handleSelectSlot = useCallback((slotInfo) => {
        if (selectable && onSelectSlot) {
            onSelectSlot(slotInfo);
        }
    }, [selectable, onSelectSlot]);

    // Memoized components
    const components = useMemo(() => ({
        event: EventComponent,
    }), []);

    return (
        <div
            className={clsx(
                'big-calendar-wrapper rounded-lg overflow-hidden bg-bg-primary',
                className
            )}
            style={{ height }}
        >
            <Calendar
                localizer={localizer}
                events={events}
                startAccessor="start"
                endAccessor="end"
                view={view}
                onView={handleViewChange}
                date={date}
                onNavigate={handleNavigate}
                onSelectEvent={onSelectEvent}
                onSelectSlot={handleSelectSlot}
                selectable={selectable}
                messages={messages}
                eventPropGetter={eventPropGetter}
                slotPropGetter={slotPropGetter}
                components={components}
                popup
                {...rest}
            />
        </div>
    );
}

// View sabitleri export
export { Views };
